import { IPaymentCard } from '../../types/global/generic.types';
import { ISaleObject } from '../../types/global/helper';

export const types = {
    OPEN_CHECKOUT: 'OPEN_CHECKOUT',
    CLOSE_CHECKOUT: 'CLOSE_CHECKOUT',
    SET_CHECKOUT_ITEMS: 'SET_CHECKOUT_ITEMS',
    ADD_CHECKOUT_ITEM: 'ADD_CHECKOUT_ITEM',
    REMOVE_CHECKOUT_ITEM: 'REMOVE_CHECKOUT_ITEM',
    UPDATE_CHECKOUT_ITEM_QUANTITY: 'UPDATE_CHECKOUT_ITEM_QUANTITY',
    APPLY_DISCOUNT: 'APPLY_DISCOUNT',
    VERIFY_DISCOUNT: 'VERIFY_DISCOUNT',
    REMOVE_DISCOUNT: 'REMOVE_DISCOUNT',
    SET_PAYMENT_METHOD: 'SET_PAYMENT_METHOD',
    PROCESS_PAYMENT: 'PROCESS_PAYMENT',
    PROCESS_PAYMENT_SUCCESS: 'PROCESS_PAYMENT_SUCCESS',
    PROCESS_PAYMENT_FAIL: 'PROCESS_PAYMENT_FAIL',
    RESET_CHECKOUT: 'RESET_CHECKOUT',
    INITIATE_REFUND: 'INITIATE_REFUND',
    INITIATE_REFUND_SUCCESS: 'INITIATE_REFUND_SUCCESS',
    INITIATE_REFUND_FAIL: 'INITIATE_REFUND_FAIL',
    CALCULATE_FEES: 'CALCULATE_FEES',
    CALCULATE_FEES_SUCCESS: 'CALCULATE_FEES_SUCCESS',
    CALCULATE_FEES_FAIL: 'CALCULATE_FEES_FAIL',
};

export interface ICheckoutItem {
    id: string;
    tierId: string;
    title: string;
    description?: string;
    price: number;
    original_price: number;
    breakdown?: Record<string, any>;
    currency: string;
    quantity: number;
    image?: string;
    details?: Array<{
        label: string;
        value: string | number;
    }>;
}

export interface IDiscount {
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
    message?: string;
}

export interface ICurrencyInfo {
    transaction_currency: string;
    original_currency?: string;
    rate?: number;
}

export interface IFeeBreakdown {
    base_amount: number;
    platform_fee_percentage: number;
    platform_fee: number;
    platform_fixed_fee: number;
    payment_processor_fee_percentage: number;
    payment_processor_fee: number;
    payment_processor_fixed_fee: number;
    total_fees: number;
    total_amount: number;
    net_amount: number;
}

export interface IFeeCalculationResponse {
    amount: number;
    currency: string;
    symbol: string;
    type: string;
    /** Net USD wallet credit (cents) when charging in another currency */
    wallet_credit_cents?: number;
    wallet_currency?: string;
    breakdown: IFeeBreakdown;
    formatted: {
        base_amount: string;
        platform_fee: string;
        platform_fixed_fee: string;
        payment_processor_fee: string;
        payment_processor_fixed_fee: string;
        total_fees: string;
        total_amount: string;
        net_amount: string;
    };
    platform_fee: number;
    platform_fixed_fee: number;
    payment_processor_fee: number;
    payment_processor_fixed_fee: number;
    total_fees: number;
    total_amount: number;
    net_amount: number;
    /** Uplift attributed to fees (cents), when returned by sales-calc for e.g. campaign_wallet */
    customer_fees?: number;
    /** Tax in cents when applicable */
    tax?: number;
}

export interface ICheckoutInitialState {
    isOpen: boolean;
    items: ICheckoutItem[];
    discount?: IDiscount;
    selectedPaymentMethod?: IPaymentCard | 'new_card';
    processing: boolean;
    error?: string;
    success?: boolean;
    currency: string;
    /** We prefer this for stripe processing */
    originalCurrency: string;
    currencyInfo?: ICurrencyInfo;
    saleObject?: ISaleObject;
    refundingTickets: Record<string, 'refund-in-progress' | 'refunded'>;
    submissionSalesObject?: ISaleObject;
    feeCalculation?: IFeeCalculationResponse;
    calculatingFees: boolean;
}

export interface IOpenCheckout {
    items: ICheckoutItem[];
    currency?: string;
    currencyInfo?: {
        transaction_currency: string;
        processing_currency?: string;
        original_currency?: string;
        rate?: number;
    };
    saleObject: ISaleObject;
}

export interface IAddCheckoutItem {
    item: ICheckoutItem;
}

export interface IRemoveCheckoutItem {
    itemId: string;
}

export interface IUpdateCheckoutItemQuantity {
    itemId: string;
    quantity: number;
}

export interface IApplyDiscount {
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
}

export interface IVerifyDiscount {
    id: string;
    discount_code: string;
    type: 'event' | 'contest' | 'audition';
}

export interface ISetPaymentMethod {
    paymentMethod: IPaymentCard | 'new_card';
}

export interface IProcessPayment {
    items: ICheckoutItem[];
    total: number;
    paymentMethod: IPaymentCard | { token: string };
    discount?: IDiscount;
    currency?: string;
    metadata?: Record<string, any>;
    saleObject: ISaleObject;
    category?: string;
}

export interface IPaidTicket {
    id: string;
    tierId: string;
    name: string;
    price: number;
    quantity: number;
    description: string;
    currency: string;
    original_currency: string;
    transaction_currency: string;
    original_price: number;
    exchange_rate: number;
}

export interface IFreeTicket {
    id: string;
    tierId: string;
    name: string;
    quantity: number;
    features: string[];
    description: string;
    original_price: 0;
    price: 0;
}
export interface TicketSummary {
    paid: IPaidTicket[];
    free: IFreeTicket[];
    totalPaidAmount: number;
    totalAmountToPayInOriginalCurrency: number;
    currency: string;
    originalCurrency: string;
}

export interface IInitiateRefund {
    saleId: string;
    reason: string;
    details: string;
}

export interface IRefundResponse {
    refund: any;
    ticket: {
        _id: string;
        state: 'refund-in-progress' | 'refunded';
    };
}

export interface ICalculateFees {
    /** Charge amount in `currency` minor units (card total base / gross preview input) */
    amount: number;
    currency: string;
    symbol: string;
    type: 'ticket' | 'submission' | 'funding' | 'tip' | 'merchandise' | 'subscription' | 'campaign_wallet' | 'tokens';
    /** Net USD wallet credit in cents — required when `currency` is not USD */
    wallet_credit_cents?: number;
    wallet_currency?: string;
}
